import {
  CaretRightOutlined,
  CheckCircleTwoTone,
  CopyOutlined,
  DownloadOutlined,
  ExclamationCircleOutlined,
  ExportOutlined,
  LoadingOutlined,
  SyncOutlined,
} from '@ant-design/icons'
import { BlobProvider, Font } from '@react-pdf/renderer'
import {
  Button,
  Col,
  Collapse,
  Drawer,
  Form,
  Input,
  message,
  Popconfirm,
  Row,
  Select,
  Spin,
  Steps,
  Tabs,
  Tag,
  Typography,
} from 'antd'
import React, { useEffect, useRef, useState } from 'react'
import {
  Batch,
  PoojaMemberData,
} from '@a4b/api/src/modules/Monetisation/Puja/types'
import '../../../../../../Style.css'
import BatchPrintView from '@a4b/ui/src/modules/monetisation/puja/molecules/BatchPrintView'
import S3Upload, {
  AllowedFormats,
} from '@a4b/ui/src/modules/monetisation/puja/molecules/S3Upload'
import { youtubeUrlRegex } from '../../../../../../utils/constants'
import SankalpNameTagging from '../SankalpNameTagging'
import { SANKALP_VIDEO_STATUS } from '../../utils/helper'

const { Panel } = Collapse

declare global {
  interface Window {
    XLSX: any
  }
}
Font.register({
  family: 'Noto Sans Hindi',
  fonts: [
    {
      src: `/Noto_Sans/NotoSans-Regular.ttf`,
    },
    {
      src: `/Noto_Sans/NotoSans-Bold.ttf`,
      fontWeight: 'bold',
    },
  ],
})
const { TabPane } = Tabs
const { Step } = Steps
const { Option } = Select

interface Props {
  onClose: () => void
  visible: boolean
  batchData: Batch | undefined
  currentStep: number
  cdnUrl: string
  preSignUrl: string
  form: any
  handleFormSubmit: (context?: { executionId: string }) => void
  isPreviewDone: boolean
  openPreview: () => void
  onFormChange: () => void
  fetchBatch: (batchCode: string) => void
  handleChangeStep: (step: number) => void
}

const BatchView: React.FC<Props> = ({
  onClose,
  visible,
  batchData,
  currentStep,
  cdnUrl,
  preSignUrl,
  form,
  handleFormSubmit,
  isPreviewDone,
  openPreview,
  onFormChange,
  fetchBatch,
  handleChangeStep,
}) => {
  const refTable: any = useRef()
  const [sankalpNameTagform] = Form.useForm()
  const [activeTab, setActiveTab] = useState('1')
  const [loading, setLoading] = useState(false)
  const isInitialized = useRef(false)

  // State for multi-day and multi-temple selections
  const [selectedTempleId, setSelectedTempleId] = useState<string | null>(null)
  const [selectedScheduleId, setSelectedScheduleId] = useState<string | null>(null)

  // Find temple and schedule data based on selections
  const selectedTempleData = batchData?.temples_data?.find(
    (t) => t.temple_id === selectedTempleId,
  )

  const selectedScheduleData = selectedTempleData?.schedules?.find(
    (s) => s.id === selectedScheduleId,
  )


  // Effect to initialize selections only once when drawer opens
  useEffect(() => {
    if (visible && batchData && (batchData.multi_temple || batchData.multi_day) && !isInitialized.current) {
      const initialTempleId = batchData.temples_data?.[0]?.temple_id ?? null
      const initialScheduleId = batchData.temples_data?.[0]?.schedules?.[0]?.id ?? null

      setSelectedTempleId(initialTempleId)
      setSelectedScheduleId(initialScheduleId)
      isInitialized.current = true

      console.log('Initializing selections:', { initialTempleId, initialScheduleId })
    } else if (!visible) {
      // Reset when drawer is closed
      setSelectedTempleId(null)
      setSelectedScheduleId(null)
      isInitialized.current = false
      console.log('Resetting selections on drawer close')
    }
  }, [visible, batchData])

  // Effect to update the 'Upload Video' form when the selected schedule changes
  useEffect(() => {
    if (selectedScheduleData) {
      form.setFieldsValue({
        videoType: selectedScheduleData.media_type || 'video',
        video_url: selectedScheduleData.video_url,
      })
    } else {
      form.resetFields(['videoType', 'video_url'])
    }
  }, [selectedScheduleData, form])

  const handleTempleChange = (templeId: string) => {
    setSelectedTempleId(templeId)

    // Auto-select the first schedule for the newly selected temple
    const newTemple = batchData?.temples_data?.find(
      (t) => t.temple_id === templeId,
    )
    const firstScheduleId = newTemple?.schedules?.[0]?.id ?? null
    setSelectedScheduleId(firstScheduleId)

  }

  const handleScheduleChange = (scheduleId: string) => {
    setSelectedScheduleId(scheduleId)
  }

  function downloadBlob(blob: any, name: string) {
    const data = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = data
    link.download = name
    link.dispatchEvent(
      new MouseEvent('click', {
        bubbles: true,
        cancelable: true,
        view: window,
      }),
    )
    setTimeout(() => {
      window.URL.revokeObjectURL(data)
      link.remove()
    }, 100)
  }

  let findMaxMembersCount: number[] = []
  if (batchData?.product_type === 'pooja') {
    const poojaMemberData = batchData?.pooja_member_data || []
    if (poojaMemberData.length > 0) {
      const max = poojaMemberData?.reduce?.(
        (prev: PoojaMemberData, current: PoojaMemberData) => {
          return prev?.members?.length > current?.members?.length ? prev : current
        },
        poojaMemberData[0],
      )
      findMaxMembersCount = Array.from({
        length: Number(max?.members?.length),
      }).map((u, i) => i)
    } else {
      findMaxMembersCount = []
    }
  }

  const handleTabChange = (key: string) => {
    setActiveTab(key)
  }

  const handleFetchLatestBatchData = (key: string) => {
    setLoading(true)
    handleTabChange(key)
    setTimeout(() => {
      fetchBatch(batchData?.batch_code!)
      setLoading(false)
      handleChangeStep(2)
      sankalpNameTagform.resetFields()
    }, 2000)
  }

  // Determine which video URL to use based on the context
  const taggedVideoUrl =
    batchData?.multi_day || batchData?.multi_temple
      ? selectedScheduleData?.tagged_url
      : batchData?.tagged_video_url

  // Determine which video tagging status to use based on the context
  const currentVideoTaggingStatus =
    batchData?.multi_day || batchData?.multi_temple
      ? selectedScheduleData?.status || SANKALP_VIDEO_STATUS.NOT_APPLICABLE
      : batchData?.video_tagging_status

  return (
    <Drawer
      width={1300}
      title={
        <Row justify="space-between">
          <Typography>Batch view</Typography>
          {currentVideoTaggingStatus === SANKALP_VIDEO_STATUS.PROCESSING && (
            <Tag icon={<ExclamationCircleOutlined />} color="warning">
              Video is still processing, please wait until it is processed
            </Tag>
          )}
        </Row>
      }
      placement="right"
      size="large"
      onClose={() => {
        sankalpNameTagform.resetFields()
        onClose()
        handleTabChange('1')
      }}
      visible={visible}
    >
      <>
        <Collapse
          bordered={false}
          defaultActiveKey={activeTab === '2' ? ['2'] : ['1', '2']}
          expandIcon={({ isActive }) => (
            <CaretRightOutlined rotate={isActive ? 90 : 0} />
          )}
          className="site-collapse-custom-collapse"
        >
          <Panel header="Batch Information" key="1" className="site-collapse-custom-panel">
            {/* Batch Info remains unchanged */}
            <Row>
              <Col span={18}>
                <Row>
                  <Col span={8}>
                    <Typography.Title level={5}>Batch Code</Typography.Title>
                  </Col>
                  <Col span={16}><Tag color='green'>{batchData?.batch_code}</Tag></Col>
                </Row>
                <Row>
                  <Col span={8}>
                    <Typography.Title level={5}>Batch ID</Typography.Title>
                  </Col>
                  <Col span={16}>{batchData?.batch_id}</Col>
                </Row>
                <Row>
                  <Col span={8}>
                    <Typography.Title level={5}>SKU Name</Typography.Title>
                  </Col>
                  <Col span={16}>{batchData?.product_name}</Col>
                </Row>
                <Row>
                  <Col span={8}>
                    <Typography.Title level={5}>No. of Items</Typography.Title>
                  </Col>
                  <Col span={16}>{batchData?.no_of_items}</Col>
                </Row>
                <Row>
                  <Col span={8}>
                    <Typography.Title level={5}>Temple Name</Typography.Title>
                  </Col>
                  <Col span={16}>{batchData?.store_name}</Col>
                </Row>
                <Row>
                  <Col span={8}>
                    <Typography.Title level={5}>Product Type</Typography.Title>
                  </Col>
                  <Col span={16}>{batchData?.product_type?.toLocaleUpperCase()}</Col>
                </Row>
              </Col>
              <Col span={6}>
                <Row style={{ height: '100%' }}>
                  <Col
                    span={24}
                    style={{ alignSelf: 'center', justifySelf: 'center' }}
                  >
                    Batch status —
                    <Tag color={'blue'} style={{ marginLeft: '5px' }}>
                      {batchData?.status?.toLocaleUpperCase()}
                    </Tag>
                  </Col>
                </Row>
              </Col>
            </Row>
          </Panel>
        </Collapse>

        {/* Dropdowns for multi-day and multi-temple selection - placed after batch information */}
        {(batchData?.multi_day || batchData?.multi_temple) && (
          <Row
            gutter={[16, 16]}
            style={{
              marginBottom: '24px',
              padding: '16px',
              backgroundColor: '#f0f2f5',
              borderRadius: '8px',
            }}
          >
            {batchData.multi_temple && (
              <Col span={12}>
                <Typography.Text strong>Select Temple: </Typography.Text>
                <Select
                  style={{ width: '100%' }}
                  value={selectedTempleId}
                  onChange={handleTempleChange}
                  placeholder="Select a temple"
                >
                  {batchData.temples_data?.map((temple) => (
                    <Option key={temple.temple_id} value={temple.temple_id}>
                      {temple.temple_name}
                    </Option>
                  ))}
                </Select>
              </Col>
            )}
            {batchData.multi_day && (
              <Col span={12}>
                <Typography.Text strong>Select Day/Date: </Typography.Text>
                <Select
                  style={{ width: '100%' }}
                  value={selectedScheduleId}
                  onChange={handleScheduleChange}
                  placeholder="Select a day"
                  disabled={!selectedTempleData}
                >
                  {selectedTempleData?.schedules?.map((schedule) => (
                    <Option key={schedule.id} value={schedule.id}>
                      {`Day ${schedule.day} (${schedule.process_date})`}
                    </Option>
                  ))}
                </Select>
              </Col>
            )}
          </Row>
        )}

        <Row>
          <Col span={24}>
            <Tabs
              activeKey={activeTab}
              onChange={handleTabChange}
              centered
            >
              <TabPane tab="Order Progression" key="1">
                {loading ? (
                  <Row style={{ width: '100%', justifyContent: 'center', alignItems: 'center' }}>
                    <Spin indicator={<LoadingOutlined spin />} size="large" />
                  </Row>
                ) : (
                  <>
                    <Steps direction="vertical" current={currentStep}>
                      <Step
                        title="Order Created"
                        description={
                          <div>{`This batch was created with ${batchData?.no_of_items} items`}</div>
                        }
                      />
                      {(batchData?.product_type === 'pooja' || batchData?.product_type === 'item') && (
                        <Step
                          title="Name Sankalp tagging"
                          description={
                            <div>
                              {currentStep === 1 ? (
                                <Row style={{ marginTop: '10px' }}>
                                  <Col span={3}>
                                    <Button
                                      type="primary"
                                      onClick={() => handleTabChange('2')}
                                    >
                                      Proceed
                                    </Button>
                                  </Col>
                                  <Col span={6}>
                                    <Button
                                      type="default"
                                      onClick={() => handleChangeStep(3)}
                                    >
                                      Skip Tagging
                                    </Button>
                                  </Col>
                                </Row>
                              ) : currentStep > 1 ? (
                                <Row style={{ marginTop: '10px' }}>
                                  <Col span={3}>
                                    <Button
                                      type="primary"
                                      onClick={() => handleTabChange('2')}
                                    >
                                      Update
                                    </Button>
                                  </Col>
                                </Row>
                              ) : (
                                <Row>
                                  <Col span={24}>
                                    Tag the Name and Gotra in raw video
                                  </Col>
                                </Row>
                              )}
                            </div>
                          }
                        />
                      )}
                      <Step
                        title="Generate final video"
                        description={
                          (batchData?.multi_day || batchData?.multi_temple) && !selectedScheduleData ? (
                            <span style={{ color: '#00000073' }}>
                              Please select a temple and day to view video status
                            </span>
                          ) : currentVideoTaggingStatus === SANKALP_VIDEO_STATUS.PROCESSED && taggedVideoUrl ? (
                            <>
                              <span style={{ color: '#00000073' }}>
                                Final video is available{' '}
                                <CheckCircleTwoTone style={{ fontSize: '20px' }} twoToneColor="#52c41a" />
                              </span>
                              <a
                                onClick={() => {
                                  message.info('Download started, check the downloads', 3)
                                  handleChangeStep(3)
                                }}
                                style={{ marginLeft: '20px' }}
                                href={taggedVideoUrl}
                                target="blank"
                              >
                                Download video <DownloadOutlined />
                              </a>
                              <Button
                                onClick={() => {
                                  navigator.clipboard.writeText(taggedVideoUrl)
                                  message.info('Link copied', 2)
                                  handleChangeStep(3)
                                }}
                                style={{ marginLeft: '20px' }}
                                type="primary"
                                icon={<CopyOutlined />}
                              >
                                Copy link
                              </Button>
                            </>
                          ) : currentVideoTaggingStatus === SANKALP_VIDEO_STATUS.PROCESSING ? (
                            <div style={{ marginTop: '5px' }}>
                              <span>
                                <Tag color="blue">
                                  Processing <SyncOutlined spin />
                                </Tag>
                              </span>
                              <span style={{ marginLeft: '5px', color: '#00000073' }}>
                                It might take 15-20 mins
                              </span>
                            </div>
                          ) : (
                            <>
                              <span style={{ color: '#00000073' }}>
                                Video not available
                              </span>
                            </>
                          )
                        }
                      />
                      <Step
                        title="Upload Video"
                        description={
                          <div>
                            <span>
                              {batchData?.product_type === 'pooja'
                                ? `Update the status here, once the pooja is done`
                                : `Update the status here, once the offering is completed`}
                            </span>
                            {batchData?.product_type === 'pooja' && currentStep === 3 && (
                              <div>
                                <Form
                                  form={form}
                                  layout="vertical"
                                  labelCol={{ span: 24 }}
                                  wrapperCol={{ span: 24 }}
                                  onFieldsChange={onFormChange}
                                >
                                  <Form.Item
                                    label="Media type"
                                    name="videoType"
                                    labelCol={{ span: 12 }}
                                    wrapperCol={{ span: 12 }}
                                    rules={[{ required: false, message: 'Please select cta media type!' }]}
                                  >
                                    <Select placeholder="Type">
                                      <Option value="video">Video</Option>
                                      <Option value="youtube">Youtube</Option>
                                    </Select>
                                  </Form.Item>
                                  <Form.Item
                                    noStyle
                                    shouldUpdate={(prev, current) => prev.videoType !== current.videoType}
                                  >
                                    {({ getFieldValue }) =>
                                      getFieldValue('videoType') === 'video' && (
                                        <Form.Item
                                          label="Video URL"
                                          name="video_url"
                                          rules={[{ min: 1, max: 1, validator: async (rules: any, values: any) => { if (values?.length < rules.min) { return Promise.reject('Please upload Video') } } }]}
                                        >
                                          <S3Upload
                                            cdnUrl={cdnUrl}
                                            preSignUrl={preSignUrl}
                                            min={1}
                                            max={1}
                                            allowedFileExtension={[AllowedFormats.MP4]}
                                          />
                                        </Form.Item>
                                      )
                                    }
                                  </Form.Item>
                                  <Form.Item
                                    noStyle
                                    shouldUpdate={(prev, current) => prev.videoType !== current.videoType}
                                  >
                                    {({ getFieldValue }) =>
                                      getFieldValue('videoType') === 'youtube' && (
                                        <Form.Item
                                          label="Youtube video link"
                                          name="video_url"
                                          rules={[{ required: true, message: 'Please input youtube video link!' }, { pattern: youtubeUrlRegex, message: 'Please input a valid Youtube video link!' }]}
                                        >
                                          <Input placeholder="Enter youtube video link" />
                                        </Form.Item>
                                      )
                                    }
                                  </Form.Item>
                                  <Row style={{ paddingTop: '20px' }}>
                                    <Col span={6}>
                                      <Form.Item shouldUpdate>
                                        {() =>
                                          form.getFieldsError().filter(({ errors }: any) => errors.length).length > 0 || !isPreviewDone ? (
                                            <Button type="primary" disabled={true}>
                                              Submit
                                            </Button>
                                          ) : (
                                            <Popconfirm
                                              placement="topLeft"
                                              title={'This video has been published on the aplication. are you sure to update this video?'}
                                              onConfirm={() =>
                                                handleFormSubmit(
                                                  batchData?.multi_day || batchData?.multi_temple
                                                    ? {
                                                      executionId: selectedScheduleId!,
                                                    }
                                                    : undefined,
                                                )
                                              }
                                              okText="Yes Submit"
                                              cancelText="No"
                                            >
                                              <Button type="primary">Submit</Button>
                                            </Popconfirm>
                                          )
                                        }
                                      </Form.Item>
                                    </Col>
                                    <Col span={6}>
                                      <Form.Item shouldUpdate className="submit">
                                        {({ getFieldValue }) => {
                                          return form.getFieldsError().filter(({ errors }: any) => errors.length).length > 0 ||
                                            (getFieldValue('videoType') === 'youtube' && getFieldValue('video_url')?.length <= 0) ? (
                                            <Button type="primary" disabled={true}>
                                              Preview
                                            </Button>
                                          ) : (
                                            <Button onClick={openPreview} type="primary">
                                              Preview
                                            </Button>
                                          )
                                        }}
                                      </Form.Item>
                                    </Col>
                                  </Row>
                                </Form>
                              </div>
                            )}
                          </div>
                        }
                      />
                    </Steps>
                    {/* The rest of the component remains largely unchanged */}
                    {batchData?.product_type === "pooja" && <table hidden ref={refTable} className='puja-table'>
                      <thead>
                        <tr>
                          <th>Pooja Name / ID</th>
                          <th>SKU ID</th>
                          <th>Gotra</th>
                          {findMaxMembersCount?.map(
                            (item: number, index: number) => (
                              <th key={index}>Name {index + 1}</th>
                            ),
                          )}

                          <th>Phone Number</th>
                          <th>WhatsApp Number</th>
                          <th>Opted for prasad</th>
                          <th>Address</th>
                          <th>Pincode</th>
                        </tr>
                      </thead>
                      <tbody>
                        {batchData?.pooja_member_data?.map(
                          (pujamembers: PoojaMemberData, index: number) => {
                            return (<tr key={index}>
                              <td>{batchData?.product_name}</td>
                              <td>{batchData?.inventory_id}</td>
                              <td>{pujamembers?.gotra}</td>
                              {findMaxMembersCount?.map(
                                (index: number) => (
                                  <>
                                    {<td key={index}>{pujamembers?.members?.[index]?.full_name}</td>}
                                  </>
                                ),
                              )}
                              <td>{pujamembers?.mobile_number}</td>
                              <td>{pujamembers?.whatsapp_number}</td>
                              <td>{pujamembers?.is_opted_for_prasad ? "Yes" : "No"}</td>
                              <td>
                                {`${pujamembers?.booking_address?.house_info},${pujamembers?.booking_address?.area_info}`}
                                <br />
                                {`${pujamembers?.booking_address?.city_name},${pujamembers?.booking_address?.landmark}`}
                                <br />
                                {`${pujamembers?.booking_address?.state_name}`}
                              </td>
                              <td>
                                {pujamembers?.booking_address?.pin_code}
                              </td>
                            </tr>)
                          }

                        )}
                      </tbody>
                    </table>}
                  </>
                )}
              </TabPane>

              {currentStep >= 1 && (
                <TabPane tab="Name Sankalp tagging" key="2">
                  <SankalpNameTagging
                    form={sankalpNameTagform}
                    batchData={batchData}
                    handleTabChange={handleFetchLatestBatchData}
                    // Pass context for multi-day/temple batches
                    selectedExecutionId={selectedScheduleId}
                    close={onClose}
                  />
                </TabPane>
              )}

              <TabPane tab="Name List" key="3">
                {/* This section remains unchanged */}
                {batchData?.product_type === 'pooja' ? (
                  <Row justify='end'>
                    <Col span={24}>
                      <Button
                        type='primary'
                        onClick={() => {
                          const wb = window?.XLSX?.utils?.table_to_book?.(
                            refTable.current,
                          )
                          window?.XLSX?.writeFile(
                            wb,
                            `namelist_${batchData?.batch_id}.xlsx`,
                          )
                        }}
                      >
                        <ExportOutlined />
                        Export Name List
                      </Button>
                    </Col>
                    <Col
                      span={24}
                      style={{ width: '800px', overflow: 'scroll' }}
                    >
                      <table ref={refTable} className='puja-table'>
                        <thead>
                          <tr>
                            <th>Pooja Name / ID</th>
                            <th>SKU ID</th>
                            <th>Gotra</th>
                            {findMaxMembersCount?.map(
                              (item: number, index: number) => (
                                <th key={index}>Name {index + 1}</th>
                              ),
                            )}

                            <th>Phone Number</th>
                            <th>WhatsApp Number</th>
                            <th>Opted for prasad</th>
                            <th>Address</th>
                            <th>Pincode</th>
                            <th>Add-ons</th>
                          </tr>
                        </thead>
                        <tbody>
                          {batchData?.pooja_member_data?.map(
                            (pujamembers: PoojaMemberData, index: number) => {
                              return (<tr key={index}>
                                <td>{batchData?.product_name}</td>
                                <td>{batchData?.inventory_id}</td>
                                <td>{pujamembers?.gotra}</td>
                                {findMaxMembersCount?.map(
                                  (index: number) => (
                                    <>
                                      {<td key={index}>{pujamembers?.members?.[index]?.full_name}</td>}
                                    </>
                                  ),
                                )}
                                <td>{pujamembers?.mobile_number}</td>
                                <td>{pujamembers?.whatsapp_number}</td>
                                <td>{pujamembers?.is_opted_for_prasad ? "Yes" : "No"}</td>
                                <td>
                                  {`${pujamembers?.booking_address?.house_info},${pujamembers?.booking_address?.area_info}`}
                                  <br />
                                  {`${pujamembers?.booking_address?.city_name},${pujamembers?.booking_address?.landmark}`}
                                  <br />
                                  {`${pujamembers?.booking_address?.state_name}`}
                                </td>
                                <td>
                                  {pujamembers?.booking_address?.pin_code}
                                </td>
                                <td>
                                  {batchData?.meta_data?.add_ons &&
                                    Object.entries(batchData?.meta_data?.add_ons)
                                      ?.filter(([key, _]) => key === pujamembers?.order_id)?.map((item: any) => (item[1]?.join(' ,  ')))}
                                </td>

                              </tr>)
                            }
                          )}
                        </tbody>
                      </table>
                    </Col>
                  </Row>
                ) : (
                  <>
                    <Row justify='end'>
                      <div>
                        <BlobProvider
                          document={
                            <BatchPrintView
                              batchData={batchData}
                              dataType='name_list'
                            />
                          }
                        >
                          {({ blob, loading }) =>
                            loading ? (
                              'Loading document...'
                            ) : (
                              <Button
                                type='primary'
                                onClick={() => {
                                  downloadBlob(
                                    blob,
                                    `namelist_${batchData?.batch_id}.pdf`,
                                  )
                                }}
                              >
                                <ExportOutlined />
                                Export Name List
                              </Button>
                            )
                          }
                        </BlobProvider>
                      </div>
                    </Row>
                    <Row>
                      <Col span={24}>
                        {' '}
                        <Typography.Title level={4}>{`Batch ID/Code : ${batchData?.batch_code || batchData?.batch_id
                          }`}</Typography.Title>
                      </Col>
                    </Row>
                    <Row>
                      <Col span={20}>
                        {batchData?.offering_names.map(
                          (name: string, index: number) => (
                            <Typography.Title level={4} key={index}>
                              {`${index + 1}. ${name}`} {batchData?.meta_data?.quantity_list?.[index] ? `X ${batchData?.meta_data?.quantity_list?.[index]}` : ""}
                            </Typography.Title>
                          ),
                        )}
                      </Col>
                    </Row>
                  </>
                )}
              </TabPane>
              {batchData?.product_type === 'prayer' && (
                <TabPane tab="Prayer List" key="4">
                  {/* This section remains unchanged */}
                  <Row>
                    <Col span={16}>
                      {batchData?.meta_data?.prayer_notes?.map(
                        (prayer_notes: string, index: number) => (
                          <Typography.Title level={4} key={index}>
                            {`${index + 1}. ${prayer_notes}`}
                          </Typography.Title>
                        ),
                      )}
                    </Col>
                    <Col span={8}>
                      <BlobProvider
                        document={
                          <BatchPrintView
                            batchData={batchData}
                            dataType='prayer_list'
                          />
                        }
                      >
                        {({ blob, loading }) =>
                          loading ? (
                            'Loading document...'
                          ) : (
                            <Button
                              type='primary'
                              onClick={() => {
                                downloadBlob(
                                  blob,
                                  `prayer_${batchData?.batch_id}.pdf`,
                                )
                              }}
                            >
                              <ExportOutlined />
                              Export Prayer
                            </Button>
                          )
                        }
                      </BlobProvider>
                    </Col>
                  </Row>
                </TabPane>
              )}
            </Tabs>
          </Col>
        </Row>
      </>
    </Drawer>
  )
}
export default BatchView